import { useCallback, useState } from 'react';
import { MDText } from 'i18n-react';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { triggerConfirmCancelBidOrOfferCTAClickedEvent } from 'Services/analytics/events';
import { cancelBid } from 'Services/api/vehicles';
import { logger } from 'Services/logger/logger';
import { useIsMounted } from 'Utilities/hooks/useIsMounted';

import biddingSectionCTAStateControllerTexts from './BiddingSectionCTAStateController.texts';
import { getSaleType } from './getSaleType';

const LocalT = new MDText(biddingSectionCTAStateControllerTexts.userHasBidOrOffered);

export const useCancelBidOrOffer = () => {
	const { bidding, state } = useVehicleData();
	const isMounted = useIsMounted();
	const { latestUserBidOrOffer } = bidding;

	const saleType = getSaleType(state);
	const saleLabel = saleType === 'SALE' ? 'offer' : 'bid';

	const [isCancellationInProgress, setIsCancellationInProgress] = useState(false);

	// This is needed as the UserHasBidOrOffered component is unmounted
	// when the state changes from cancelBid
	const disableLoading = () => {
		if (isMounted) {
			setIsCancellationInProgress(false);
		}
	};

	const onCancelBidOrOffer = useCallback(async () => {
		if (isCancellationInProgress) {
			return;
		}

		setIsCancellationInProgress(true);

		try {
			if (!latestUserBidOrOffer) {
				throw new Error('No bid or offer found');
			}
			await cancelBid(`${latestUserBidOrOffer.bidOrOfferId}`);

			triggerConfirmCancelBidOrOfferCTAClickedEvent({ purchaseType: saleLabel, success: true });

			disableLoading();
			toast({ content: LocalT.translate(`cancellation.success.${saleType}`), variant: 'success' });
		} catch (e) {
			const error = e instanceof Error ? e : new Error(`There was an error cancelling your ${saleLabel}.`);
			logger.error({ error, message: error.message, scope: 'useCancelBidOrOffer' });

			triggerConfirmCancelBidOrOfferCTAClickedEvent({
				errorMessage: error.message,
				purchaseType: saleLabel,
				success: false,
			});

			disableLoading();
			toast({ content: LocalT.translate(`cancellation.failure.${saleType}`), variant: 'error' });
		}
	}, [isCancellationInProgress, latestUserBidOrOffer, disableLoading, saleType, saleLabel]);

	return { isCancellationInProgress, onCancelBidOrOffer };
};
